import type { ReactNode } from 'react';
import { rotatedBounds } from '../core/model/bounds';
import { getLayer } from '../core/model/document';
import { isGroup } from '../core/model/tree';
import type { EditorDocument, Rect } from '../core/model/types';
import {
  isMovable,
  jointMembers,
  type LayerTransform,
  layerBounds,
  unionBounds,
  unionRect,
} from '../core/move';
import { HANDLES } from '../core/selection';
import type { Viewport } from '../core/view';
import { useEditorStore } from '../state/editorStore';
import { docToStage } from './pointer';

/**
 * The transform outline above the selection.
 *
 * DOM, not canvas — same choice as the marquee: the browser gives crisp
 * hairlines at any zoom, and the geometry comes from the same pure
 * `layerBounds` the hit-testing uses, so outline and click always agree.
 *
 * One box, three owners (Iteration 15):
 *  - a lone layer keeps its own rotated box, exactly as since Iteration 8;
 *  - a multi-selection gets the union box of its movable members — corners,
 *    edges (only when every member is unrotated) and the knob act on the set;
 *  - a group selected in the panel gets the union box of its leaves, because
 *    a group is its members.
 *
 * During a drag the live box comes from the store's `layerPreviews` — the
 * drag's full truth — and the per-member outlines stand down: the engine's
 * per-layer previews are what is true then, and DOM outlines computed from
 * the document would lag a frame behind them.
 */

const HANDLE_STYLE: Record<(typeof HANDLES)[number], { left: string; top: string }> = {
  nw: { left: '0%', top: '0%' },
  n: { left: '50%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  e: { left: '100%', top: '50%' },
  se: { left: '100%', top: '100%' },
  s: { left: '50%', top: '100%' },
  sw: { left: '0%', top: '100%' },
  w: { left: '0%', top: '50%' },
};

export function TransformOverlay() {
  const doc = useEditorStore((s) => s.doc);
  const viewport = useEditorStore((s) => s.viewport);
  const activeLayerId = useEditorStore((s) => s.activeLayerId);
  const selectedIds = useEditorStore((s) => s.selectedIds);
  const preview = useEditorStore((s) => s.layerPreview);
  const previews = useEditorStore((s) => s.layerPreviews);

  const layer = activeLayerId ? getLayer(doc, activeLayerId) : undefined;
  if (!layer?.visible) return null;

  // Mid-drag: the live union of every previewed member. The members' own
  // outlines stand down for the length of the gesture.
  if (previews && previews.length > 0) {
    const live = liveUnion(doc, previews);
    if (live) {
      return (
        <Overlay>
          <Box
            rect={live}
            angle={0}
            edges={previews.every((one) => one.angle % 360 === 0)}
            viewport={viewport}
          />
        </Overlay>
      );
    }
  }

  // A group is its members: the union box of the visible, unlocked leaves it
  // would move. (Empty or fully-locked groups show no box — nothing to grab.)
  if (isGroup(layer)) {
    const members = jointMembers(doc, new Set([layer.id]));
    const bounds = unionBounds(members);
    if (!bounds) return null;
    return (
      <Overlay>
        <Box
          rect={bounds}
          angle={0}
          edges={members.every((member) => member.start.angle % 360 === 0)}
          viewport={viewport}
        />
      </Overlay>
    );
  }

  if (!isMovable(layer)) return null;
  const bounds = layerBounds(layer);
  if (!bounds) return null;

  // Over a multi-selection at rest, the box is the union of the members and
  // every non-primary member gets a lighter outline.
  if (selectedIds.size > 1) {
    const members = jointMembers(doc, selectedIds);
    const union = unionBounds(members);
    if (members.length > 1 && union) {
      const others = members.filter((member) => member.id !== layer.id);
      return (
        <Overlay>
          {others.map((member) => {
            const memberAngle = getLayer(doc, member.id)?.angle ?? 0;
            return (
              <MemberOutline
                key={member.id}
                id={member.id}
                rect={member.bounds}
                angle={memberAngle}
                viewport={viewport}
              />
            );
          })}
          <Box
            rect={union}
            angle={0}
            edges={members.every((member) => member.start.angle % 360 === 0)}
            viewport={viewport}
          />
        </Overlay>
      );
    }
  }

  // The lone layer: its own rotated box, live with its preview mid-drag.
  const live: LayerTransform =
    preview && preview.layerId === layer.id
      ? {
          x: preview.x,
          y: preview.y,
          scaleX: preview.scaleX,
          scaleY: preview.scaleY,
          angle: preview.angle,
        }
      : { x: layer.x, y: layer.y, scaleX: layer.scaleX, scaleY: layer.scaleY, angle: layer.angle };

  const shown: Rect = {
    x: live.x,
    y: live.y,
    width: bounds.width * (live.scaleX / (layer.scaleX || 1)),
    height: bounds.height * (live.scaleY / (layer.scaleY || 1)),
  };

  return (
    <Overlay>
      <Box rect={shown} angle={live.angle} edges viewport={viewport} />
    </Overlay>
  );
}

function Overlay({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="transform-overlay"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {children}
    </div>
  );
}

/**
 * The box with its handles. A single layer's box rotates with the layer; the
 * joint box never does (members keep their own angles), so `angle` is 0 there
 * and the knob rides an axis-aligned top edge.
 */
function Box({
  rect,
  angle,
  edges,
  viewport,
}: {
  rect: Rect;
  angle: number;
  edges: boolean;
  viewport: Viewport;
}) {
  const origin = docToStage({ x: rect.x, y: rect.y }, viewport);
  return (
    <div
      data-testid="transform-box"
      className="absolute"
      style={{
        left: origin.x,
        top: origin.y,
        width: rect.width * viewport.zoom,
        height: rect.height * viewport.zoom,
        transform: angle ? `rotate(${angle}deg)` : undefined,
      }}
    >
      <div
        className="absolute inset-0"
        style={{ boxShadow: '0 0 0 1px var(--color-accent-strong)' }}
      />
      {/* The rotate knob: above top-centre, orbiting with the box because
          it lives inside the rotated box. The hook hit-tests it in document
          space; this is just where it draws. */}
      <span
        data-testid="rotate-handle"
        className="absolute left-1/2 h-[9px] w-[9px] -translate-x-1/2 rounded-full border border-[var(--color-accent-strong)] bg-surface"
        style={{ top: -24 - 4.5 }}
      />
      <span
        className="absolute left-1/2 w-px -translate-x-1/2 bg-[var(--color-accent-strong)]"
        style={{ top: -24, height: 24 }}
      />
      {HANDLES.filter((handle) => edges || handle.length === 2).map((handle) => (
        <span
          key={handle}
          data-testid={`scale-handle-${handle}`}
          className="absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-[1px] border border-[var(--color-accent-strong)] bg-surface"
          style={HANDLE_STYLE[handle]}
        />
      ))}
    </div>
  );
}

/** The lighter outline a non-primary member wears while the set is at rest. */
function MemberOutline({
  id,
  rect,
  angle,
  viewport,
}: {
  id: string;
  rect: Rect;
  angle: number;
  viewport: Viewport;
}) {
  const origin = docToStage({ x: rect.x, y: rect.y }, viewport);
  return (
    <div
      data-testid={`transform-member-${id}`}
      className="absolute"
      style={{
        left: origin.x,
        top: origin.y,
        width: rect.width * viewport.zoom,
        height: rect.height * viewport.zoom,
        transform: `rotate(${angle}deg)`,
      }}
    >
      <div className="absolute inset-0" style={{ boxShadow: '0 0 0 1px var(--color-accent)' }} />
    </div>
  );
}

/**
 * The union of every previewed member's drawn bounds, rotation included —
 * what the box follows during a joint drag. The drawn size is the member's
 * document bounds scaled by the ratio its preview applies, the same trick
 * the lone-layer box uses.
 */
function liveUnion(
  doc: EditorDocument,
  previews: readonly (LayerTransform & { layerId: string })[],
): Rect | null {
  let union: Rect | null = null;
  for (const one of previews) {
    const layer = getLayer(doc, one.layerId);
    if (!layer) continue;
    const base = layerBounds(layer);
    if (!base) continue;
    const shown: Rect = {
      x: one.x,
      y: one.y,
      width: base.width * (one.scaleX / (layer.scaleX || 1)),
      height: base.height * (one.scaleY / (layer.scaleY || 1)),
    };
    union = unionRect(union, rotatedBounds(shown, one.angle));
  }
  return union;
}
